"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X, UserCheck, Zap, ShieldCheck, Coins, Sparkles, ArrowRight, Clock, Target } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = [{
    title: "Direct Access to Me",
    icon: UserCheck,
    consultantText: "You work directly with me—Yassine Essaadi. Every strategy, audit, and technical optimization is executed personally by a seasoned expert.",
    agencyText: "Your account is pitched by senior executives, then passed to junior team members or offshored account managers.",
    blushHighlight: "100% direct communication & personal accountability."
  }, {
    title: "Bespoke SEO Strategy",
    icon: Target,
    consultantText: "I build tailored, commercial-first SEO campaigns designed around your specific technical stack, business goals, and ROI targets.",
    agencyText: "Rigid templates, automated audit checklists, and standardized monthly deliverable packages.",
    blushHighlight: "Zero generic templates or copy-paste playbooks."
  }, {
    title: "Transparent Cost Efficiency",
    icon: Coins,
    consultantText: "Every pound goes directly into hands-on expert SEO execution. You pay for senior expertise without funding fancy agency offices.",
    agencyText: "High monthly retainers inflated by agency overhead, account manager salaries, and sales commissions.",
    blushHighlight: "Maximum ROI with lean, expert-driven expenditure."
  }, {
    title: "Agility & Rapid Execution",
    icon: Zap,
    consultantText: "Decisions and technical fixes happen quickly. When Google updates its algorithm, I adapt your campaign within hours.",
    agencyText: "Multiple layers of management, ticket queues, approval bottlenecks, and slow turnarounds.",
    blushHighlight: "Direct execution without bureaucratic friction."
  }, {
    title: "Capped Client Focus",
    icon: Clock,
    consultantText: "I deliberately limit the number of active clients I partner with to ensure deep focus, high responsiveness, and quality work.",
    agencyText: "Agencies push specialists to manage 15–20+ client accounts simultaneously to maximize profit margins.",
    blushHighlight: "Dedicated focus on your organic growth."
  }, {
    title: "Honest & Practical Reporting",
    icon: ShieldCheck,
    consultantText: "Clear reporting focused on organic revenue, lead quality, non-brand rankings, and actionable search growth.",
    agencyText: "Heavy decks filled with vanity metrics, traffic spikes without conversions, and automated PDF summaries.",
    blushHighlight: "Honest feedback and measurable business impact."
  }];
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground relative py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold rounded-full border border-accent/20">
            <Sparkles className="size-3.5 mr-1 text-accent" />
            Independent SEO Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif tracking-tight text-foreground mb-6">
            Why Work Directly With Me Instead of an SEO Agency?
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            I offer a senior-level alternative to high-overhead digital agencies. When you partner with YE Consultancy, 
            you get senior SEO expertise, total transparency, and swift execution—without agency fluff.
          </p>
        </div>

        {}
        <div className="mb-14 rounded-2xl bg-secondary/80 border border-accent/20 p-6 sm:p-8 shadow-xs text-center md:text-left md:flex items-center justify-between gap-6">
          <div className="space-y-2 mb-4 md:mb-0">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <UserCheck className="size-4 text-accent" />
              <span>Personal Brand Guarantee</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              {"\"You work directly with me—from initial strategy to technical execution.\""}
            </h3>
            <p className="text-sm text-muted-foreground max-w-2xl">
              No junior account managers, no middleman communications, and no delegated deliverables. Just direct access to Yassine Essaadi.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild variant="default" size="lg" className="rounded-xl font-medium shadow-sm">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-accent/40 rounded-2xl flex flex-col justify-between overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="size-12 rounded-xl bg-secondary flex items-center justify-center text-primary border border-accent/20">
                        <Icon className="size-6 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-[13px] font-medium border-accent/30 bg-secondary/50 text-foreground">
                        Point {i + 1}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold font-serif text-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                    {}
                    <div className="space-y-3">
                      {}
                      <div className="p-3.5 rounded-xl bg-background border border-border/80">
                        <div className="flex items-start gap-2.5">
                          <div className="size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                            <Check className="size-3.5 stroke-[3]" />
                          </div>
                          <div>
                            <span className="text-xs font-semibold uppercase tracking-wider text-primary block mb-0.5">
                              YE Consultancy
                            </span>
                            <p className="text-sm text-foreground leading-snug">
                              {benefit.consultantText}
                            </p>
                          </div>
                        </div>
                      </div>

                      {}
                      <div className="p-3 rounded-xl bg-muted/40 border border-border/50">
                        <div className="flex items-start gap-2.5">
                          <div className="size-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 mt-0.5">
                            <X className="size-3.5 stroke-[3]" />
                          </div>
                          <div>
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-0.5">
                              Typical SEO Agency
                            </span>
                            <p className="text-xs text-muted-foreground leading-snug">
                              {benefit.agencyText}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="pt-2 border-t border-border/60">
                      <div className="text-xs font-medium text-foreground bg-secondary/70 p-2.5 rounded-lg border border-accent/20 flex items-center gap-2">
                        <Sparkles className="size-3.5 text-accent shrink-0" />
                        <span>{benefit.blushHighlight}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 text-center bg-card rounded-2xl border border-border p-8 sm:p-12 shadow-sm max-w-4xl mx-auto">
          <h3 className="text-xl sm:text-2xl font-semibold font-serif text-foreground mb-4">
            Ready for a Direct, Senior-Level SEO Partnership?
          </h3>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto mb-8">
            Let us discuss your search visibility, technical structure, and growth opportunities. 
            No high-pressure sales reps—just an honest conversation with an independent consultant.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-xl px-8 font-medium w-full sm:w-auto shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-xl px-8 font-medium w-full sm:w-auto border-border">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}